import { type CreateIncidentRequest, IncidentSeverity } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { dateTimeSchema, P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useCreateIncidentMutation } from '../../graphql/hooks/useCreateIncidentMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { IncidentForm } from './IncidentForm';

type FormValues = CreateIncidentRequest & {
  participant?: {
    id: string;
  } | null;
  reviewers: string[];
};

const schema: z.ZodSchema<Partial<FormValues>> = z.object({
  description: z.string('Please enter a description'),
  setting: z.string('Please select a setting'),
  participant: z
    .object({
      id: z.string(),
    })
    .nullish(),
  reviewers: z.array(z.string()).nonempty('Please select at least one reviewer'),
  severity: z.enum(IncidentSeverity, { error: 'Please select incident severity' }),
  timestamp: dateTimeSchema,
  ndisReportableIncident: z.boolean(),
  witnesses: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      phone: z.string().nullish(),
      email: z.email().nullish(),
      description: z.string().nullish(),
    }),
  ),
  labelIds: z.array(z.string()),
  categoryId: z.string('Please select a category'),
});

export const IncidentCreateModal = createModal((props) => {
  const { modal } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, remove, visible } = modal;

  const methods = useForm<FormValues>({
    defaultValues: {
      description: undefined,
      setting: undefined,
      participant: undefined,
      reviewers: [],
      severity: undefined,
      ndisReportableIncident: false,
      timestamp: undefined,
      witnesses: [],
      labelIds: [],
      categoryId: undefined,
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useCreateIncidentMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const { participant, reviewers, ...rest } = values;

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          request: {
            ...P.deepTrim(rest),
            participantId: participant?.id,
            reviewerIds: reviewers,
          },
        })
        .then(() => {
          hide();
          successToast('Incident created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating incident: ${err.message}`);
        });
    },
    [mutation, organization.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='xl'>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Add an incident'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <IncidentForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
